/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { OuiButton } from '../../button';
import { OuiI18n } from '../../i18n';
import { OuiToolTip } from '../../tool_tip';
export class OuiSuperUpdateButton extends Component {
  static defaultProps = {
    needsUpdate: false,
    isLoading: false,
    isDisabled: false,
    showTooltip: false,
    compressed: false
  };
  _isMounted = false;
  tooltip = null;

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate() {
    if (this.props.showTooltip && this.props.needsUpdate && !this.props.isDisabled && !this.props.isLoading) {
      this.showTooltip();
      this.tooltipTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 2000);
    }
  }

  setTootipRef = node => {
    this.tooltip = node;
  };
  showTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }

    this.tooltip.showToolTip();
  };
  hideTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }

    this.tooltip.hideToolTip();
  };

  render() {
    const {
      className,
      needsUpdate,
      isLoading,
      isDisabled,
      onClick,
      toolTipProps,
      showTooltip,
      compressed,
      textProps: restTextProps,
      ...rest
    } = this.props;
    const classes = classNames('ouiSuperUpdateButton', className);
    let buttonText = <OuiI18n token="ouiSuperUpdateButton.refreshButtonLabel" default="刷新" />;

    if (needsUpdate || isLoading) {
      buttonText = isLoading ? <OuiI18n token="ouiSuperUpdateButton.updatingButtonLabel" default="正在更新" /> : <OuiI18n token="ouiSuperUpdateButton.updateButtonLabel" default="更新" />;
    }

    let tooltipContent;

    if (isDisabled) {
      tooltipContent = <OuiI18n token="ouiSuperUpdateButton.cannotUpdateTooltip" default="无法更新" />;
    } else if (needsUpdate && !isLoading) {
      tooltipContent = <OuiI18n token="ouiSuperUpdateButton.clickToApplyTooltip" default="点击应用" />;
    }

    return <OuiToolTip ref={this.setTootipRef} content={tooltipContent} position="bottom" {...toolTipProps}>
        <OuiButton className={classes} color={needsUpdate || isLoading ? 'success' : 'primary'} iconType="refresh" textProps={{ ...restTextProps,
        className: classNames('ouiSuperUpdateButton__text', restTextProps?.className)
      }} isDisabled={isDisabled} onClick={onClick} isLoading={isLoading} size={compressed ? 's' : 'm'} {...rest}>
          {buttonText}
        </OuiButton>
      </OuiToolTip>;
  }

}
OuiSuperUpdateButton.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  needsUpdate: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,

  /**
       * Passes props to `OuiToolTip`
       */
  toolTipProps: PropTypes.shape({
    /**
       * Passes onto the the trigger.
       */
    anchorClassName: PropTypes.string,

    /**
       * The in-view trigger for your tooltip.
       */
    children: PropTypes.element.isRequired,

    /**
       * Passes onto the tooltip itself, not the trigger.
       */
    className: PropTypes.string,

    /**
       * The main content of your tooltip.
       */
    content: PropTypes.node,

    /**
       * Common display alternatives for the anchor wrapper
       */
    display: PropTypes.oneOf(["inlineBlock", "block"]),

    /**
       * Delay before showing tooltip. Good for repeatable items.
       */
    delay: PropTypes.oneOf(["regular", "long"]).isRequired,

    /**
       * An optional title for your tooltip.
       */
    title: PropTypes.node,

    /**
       * Unless you provide one, this will be randomly generated.
       */
    id: PropTypes.string,

    /**
       * Suggested position. If there is not enough room for it this will be changed.
       */
    position: PropTypes.oneOf(["top", "right", "bottom", "left"]).isRequired,

    /**
       * If supplied, called when mouse movement causes the tool tip to be
       * hidden.
       */
    onMouseOut: PropTypes.func
  }),

  /**
       * Show the "Click to apply" tooltip
       */
  showTooltip: PropTypes.bool.isRequired,
  compressed: PropTypes.bool
};